const API_BASE =
  (process.env.REACT_APP_API_URL as string) ||
  'https://api.agentumi.xyz/api/finance';

async function request<T>(path: string, opts?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { 'Content-Type': 'application/json' },
    ...opts,
  });
  if (!res.ok) throw new Error(`API ${res.status}: ${res.statusText}`);
  return res.json();
}

/* ── Escrow ─────────────────────────────────────── */

export interface Escrow {
  id?: string;
  taskId: string;
  agentId: string;
  amount: number;
  token: string;
  status: 'locked' | 'released' | 'disputed';
  createdAt?: string;
}

export function createEscrow(data: Omit<Escrow, 'id' | 'createdAt'>) {
  return request<Escrow>('/escrow', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function fetchEscrows() {
  return request<Escrow[]>('/escrows');
}

/* ── Settlement ─────────────────────────────────── */

export interface Settlement {
  id?: string;
  escrowId: string;
  agentId: string;
  amount: number;
  token: string;
  status: 'pending' | 'completed' | 'failed';
  completedAt?: string;
  createdAt?: string;
}

export function createSettlement(data: Omit<Settlement, 'id' | 'createdAt'>) {
  return request<Settlement>('/settlement', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function fetchSettlements() {
  return request<Settlement[]>('/settlements');
}

/* ── Dispute ────────────────────────────────────── */

export interface Dispute {
  id?: string;
  escrowId: string;
  filedBy: string;
  reason: string;
  status: 'open' | 'resolved' | 'rejected';
  resolution?: string;
  createdAt?: string;
}

export function createDispute(data: Omit<Dispute, 'id' | 'createdAt'>) {
  return request<Dispute>('/dispute', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function fetchDisputes() {
  return request<Dispute[]>('/disputes');
}

/* ── Proof ──────────────────────────────────────── */

export interface FinanceProof {
  id?: string;
  settlementId: string;
  hash: string;
  verifier: string;
  createdAt?: string;
}

export function createProof(data: Omit<FinanceProof, 'id' | 'createdAt'>) {
  return request<FinanceProof>('/proof', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function fetchProofs() {
  return request<FinanceProof[]>('/proofs');
}

/* ── Aggregated Stats ───────────────────────────── */

export interface FinanceStats {
  totalEscrows: number;
  activeSettlements: number;
  openDisputes: number;
  totalVolume: string;
}

export async function fetchFinanceStats(): Promise<FinanceStats> {
  try {
    const [escrows, settlements, disputes] = await Promise.all([
      fetchEscrows(),
      fetchSettlements(),
      fetchDisputes(),
    ]);
    const totalVolume = escrows.reduce((s, e) => s + (e.amount || 0), 0);
    return {
      totalEscrows: escrows.length,
      activeSettlements: settlements.filter((s) => s.status === 'pending').length,
      openDisputes: disputes.filter((d) => d.status === 'open').length,
      totalVolume: totalVolume.toLocaleString(),
    };
  } catch {
    return { totalEscrows: 0, activeSettlements: 0, openDisputes: 0, totalVolume: '0' };
  }
}
